import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  User,
  Star,
  Bell,
  Gift,
  LayoutGrid,
  ChevronRight,
  ChevronDown,
  Settings,
} from "lucide-react";
import useConfig from "@/hooks/useConfig";
import PageRoutes from "@/utils/constants";
import { cn } from "@/v2/lib/utils"; // Assuming cn utility is available in v2/lib/utils

export const sidebarLinks = [
  { name: "Profile Information", path: PageRoutes.Setting, icon: User },
  { name: "Account", path: PageRoutes.AccountPage, icon: Settings },
  { name: "Favorites Manager", path: PageRoutes.FavoritesManager, icon: Star },
  { name: "Plans", path: PageRoutes.Packages, icon: LayoutGrid },
  // Purchase History and Billing & Payments used to link here too, but both
  // routed to the AccountComingSoon placeholder (App.tsx) - the real,
  // working versions of both live inline on the Account page itself
  // (PurchaseHistoryCard / PaymentMethodCard), so the dead nav entries were
  // removed rather than pointing at an unfinished page.
  // Gifting is surfaced by the "Buy a Gift" promo card below, so no duplicate
  // nav entry for the same /buy-gift route.
  { name: "Notifications", path: PageRoutes.Alert, icon: Bell },
];

export function LeftSidebar() {
  const location = useLocation();
  const config = useConfig();
  // Below lg the rail becomes a collapsible dropdown; collapsed by default.
  const [open, setOpen] = useState(false);
  // /account and /plans only make sense for tenants that sell plans
  // (config.planpage). A free-only tenant like dexter (all games FFV) has
  // neither, so drop both links: Account would dead-end at NoPageFound and
  // Plans has nothing to show.
  const planlessHidden = [PageRoutes.AccountPage, PageRoutes.Packages];
  const links = sidebarLinks.filter(
    (link) => !planlessHidden.includes(link.path) || Boolean(config?.planpage)
  );
  // Boundary-aware so a shorter path isn't treated as active on an unrelated
  // deeper route. Used for both the rail highlight and the mobile trigger so
  // they never disagree.
  const matchesPath = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");
  // If a link's path happens to be a prefix of another link's path, both
  // could match a given pathname via matchesPath - pick the longest (most
  // specific) match as the single active link rather than letting both
  // light up. On mobile the collapsed trigger reflects that section, so it
  // reads e.g. "Favorites Manager" instead of a static "My Account". Falls
  // back to the section label when the route isn't one of the nav items.
  const activeLink = links.reduce<(typeof links)[number] | undefined>(
    (best, link) => {
      if (!matchesPath(link.path)) return best;
      if (!best || link.path.length > best.path.length) return link;
      return best;
    },
    undefined,
  );
  const TriggerIcon = activeLink?.icon ?? Settings;

  return (
    <div className="w-full flex-shrink-0 flex flex-col py-2 lg:pt-6 lg:pb-16 px-2 lg:px-3 bg-white lg:h-full lg:overflow-y-auto">
      {/* Mobile: dropdown trigger. Hidden at lg where the full rail shows. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="my-account-nav"
        className="lg:hidden flex items-center gap-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-3 text-left"
      >
        <TriggerIcon
          className="w-5 h-5 flex-shrink-0 text-action"
          strokeWidth={2}
        />
        <span className="flex-1 font-bold text-slate-900 text-[15px]">
          {activeLink?.name ?? "My Account"}
        </span>
        <ChevronDown
          className={cn(
            "w-5 h-5 text-slate-500 transition-transform",
            open && "rotate-180",
          )}
          strokeWidth={2}
        />
      </button>

      <h3
        className="hidden lg:block font-bold text-slate-700 px-4 uppercase tracking-wide mb-2"
        style={{ fontSize: "13px" }}
      >
        MY ACCOUNT
      </h3>

      {/* One nav for both layouts: revealed on mobile only when open, always
          shown at lg+. Rendered once so each link stays unique in the DOM. A
          <div> (not <nav>) because an unlayered `nav { display:block }` reset
          outranks the `hidden` utility, so a <nav> can't collapse on mobile. */}
      <div
        role="navigation"
        aria-label="My Account menu"
        id="my-account-nav"
        className={cn(
          "flex-1 space-y-2 lg:space-y-1 mt-2 lg:mt-0",
          open ? "block" : "hidden",
          "lg:block",
        )}
      >
        {links.map((link) => {
          const Icon = link.icon;
          const isActive = link === activeLink;

          return (
            <Link
              key={link.name}
              to={link.path}
              aria-label={link.name}
              onClick={() => setOpen(false)}
              className={cn(
                "flex items-center justify-start gap-3 lg:gap-4 px-3 py-2 rounded-lg text-sm transition-colors",
                isActive
                  ? "font-bold text-action bg-action-light"
                  : "bg-transparent text-slate-600 font-medium hover:bg-slate-50 hover:text-slate-900",
              )}
            >
              <Icon
                aria-hidden="true"
                className={cn(
                  "w-5 h-5 flex-shrink-0",
                  isActive ? "text-action" : "text-slate-500",
                )}
                strokeWidth={isActive ? 2.5 : 1.75}
              />
              <span>{link.name}</span>
            </Link>
          );
        })}
      </div>

      {/* Gifting is a paid-plan feature - hide the promo for free-only tenants
          (config.planpage off, e.g. dexter). */}
      {config?.planpage && (
        <div className="hidden lg:block border border-slate-100 rounded-xl p-3 bg-white">
          <div className="bg-accent w-10 h-10 rounded-full flex items-center justify-center mb-3">
            <Gift
              className="w-5 h-5"
              strokeWidth={2}
              style={{ color: "var(--v2-action)" }}
            />
          </div>
          <div className="font-bold text-slate-900 text-[15px] mb-2">
            Give the gift of hockey
          </div>
          <div className="text-slate-500 text-sm leading-relaxed mb-4">
            Share the game they love with a gift subscription.
          </div>
          <Link
            to={PageRoutes.GiftPage}
            className="font-semibold text-sm flex items-center no-underline gap-1.5"
            style={{ color: "var(--v2-action)" }}
          >
            Buy a Gift
            <ChevronRight className="w-4 h-4" strokeWidth={2} />
          </Link>
        </div>
      )}
    </div>
  );
}
